// Thirteen Colonies quiz fixture.
// POC data — not persisted, no grading beyond client-side tally.

const ALL_COLONIES = [
  'MA', 'NH', 'CT', 'RI',
  'NY', 'NJ', 'PA', 'DE',
  'MD', 'VA', 'NC', 'SC', 'GA',
];

export default {
  slug: 'thirteen-colonies',
  title: 'The Thirteen Colonies',
  course_context: 'AP US History',
  geo_source: 'thirteen_colonies',
  questions: [
    {
      id: 'q1',
      type: 'map_identify',
      prompt: 'Click the colony founded by William Penn as a haven for Quakers.',
      selectable_entity_ids: ALL_COLONIES,
      correct_entity_id: 'PA',
      explanation:
        "Pennsylvania (founded 1681) was granted to William Penn by Charles II as repayment of a debt. Penn established it as a refuge for Quakers and other persecuted religious groups, leading to one of the most religiously diverse colonies.",
    },
    {
      id: 'q2',
      type: 'map_identify',
      prompt:
        'Click the last of the thirteen colonies to be founded — established in 1732 as a buffer against Spanish Florida.',
      selectable_entity_ids: ALL_COLONIES,
      correct_entity_id: 'GA',
      explanation:
        "Georgia was founded in 1732 by James Oglethorpe under a charter from George II. It was designed as both a philanthropic haven for English debtors and a military buffer protecting the Carolinas from Spanish Florida.",
    },
    {
      id: 'q3',
      type: 'map_identify',
      prompt:
        'Click the colony founded in 1634 as a haven for English Catholics under the Calvert family.',
      selectable_entity_ids: ALL_COLONIES,
      correct_entity_id: 'MD',
      explanation:
        "Maryland was chartered to Cecil Calvert (Lord Baltimore) in 1632 and settled in 1634. It was intended as a refuge for English Catholics, who faced persecution at home. The 1649 Act of Toleration codified religious freedom for all Trinitarian Christians.",
    },
    {
      id: 'q4',
      type: 'map_mcq',
      prompt:
        'What economic system primarily defined the highlighted colonies by the mid-1700s?',
      highlighted_entity_ids: ['VA', 'NC', 'SC', 'GA', 'MD'],
      options: [
        { id: 'a', text: 'Subsistence farming on small family plots', correct: false },
        { id: 'b', text: 'Plantation agriculture dependent on enslaved labor', correct: true },
        { id: 'c', text: 'Shipbuilding, fishing, and Atlantic trade', correct: false },
        { id: 'd', text: 'Textile manufacturing and ironworks', correct: false },
      ],
      explanation:
        "By the 1700s, the Southern colonies' economies centered on cash-crop plantations — tobacco in the Chesapeake, rice and indigo further south — worked by enslaved Africans whose labor created the wealth that defined the region's social and political structure.",
    },
    {
      id: 'q5',
      type: 'map_mcq',
      prompt: 'What primarily defined the New England economy of the highlighted colonies?',
      highlighted_entity_ids: ['MA', 'NH', 'CT', 'RI'],
      options: [
        { id: 'a', text: 'Cash-crop plantations worked by enslaved labor', correct: false },
        { id: 'b', text: 'Shipbuilding, fishing, and Atlantic maritime trade', correct: true },
        { id: 'c', text: 'A fur trade dependent on French Canadian allies', correct: false },
        { id: 'd', text: 'Large-scale sugar refining for European markets', correct: false },
      ],
      explanation:
        "New England's rocky soil and short growing season discouraged plantation agriculture. Instead, deep harbors, abundant timber, and proximity to the Grand Banks fostered a maritime economy: shipbuilding, cod fishing, and a merchant class trading within the triangular Atlantic.",
    },
    {
      id: 'q6',
      type: 'map_mcq',
      prompt: 'Which best describes the population of the highlighted Middle Colonies?',
      highlighted_entity_ids: ['NY', 'NJ', 'PA', 'DE'],
      options: [
        {
          id: 'a',
          text: 'Overwhelmingly English Puritan, with tight religious uniformity',
          correct: false,
        },
        {
          id: 'b',
          text: 'Ethnically and religiously diverse — Dutch, German, Scots-Irish, and Quaker populations alongside English settlers',
          correct: true,
        },
        {
          id: 'c',
          text: 'Primarily enslaved Africans working tobacco plantations',
          correct: false,
        },
        {
          id: 'd',
          text: 'Almost entirely French Huguenot refugees',
          correct: false,
        },
      ],
      explanation:
        "The Middle Colonies were the most diverse region of colonial North America. New York retained large Dutch populations from its New Netherland origins; Pennsylvania's tolerance drew Germans, Scots-Irish, and Quakers; smaller Swedish and Finnish communities persisted in Delaware.",
    },
    {
      id: 'q7',
      type: 'map_short_answer',
      prompt:
        'In 2–3 sentences, describe the labor system that emerged in the highlighted colony in the 1600s and how it changed by the early 1700s.',
      highlighted_entity_ids: ['VA'],
      model_answer:
        "Virginia initially relied on indentured servitude, with English laborers trading four to seven years of work for passage and eventual land. After Bacon's Rebellion (1676), shifting Atlantic conditions, and a steadier supply of enslaved Africans, planters shifted decisively to chattel slavery. By the early 1700s, racial slavery was codified into law through the Virginia Slave Codes, making slavery hereditary and permanent along racial lines.",
    },
    {
      id: 'q_locate_1',
      type: 'map_locate',
      prompt: 'Find and click Rhode Island on the map.',
      selectable_entity_ids: ALL_COLONIES,
      correct_entity_id: 'RI',
      explanation:
        'Rhode Island was the smallest of the thirteen colonies and one of the most independent-minded. Founded by Roger Williams in 1636 after his banishment from Massachusetts Bay Colony, it became known for religious toleration and skepticism of centralized authority — it was the last colony to ratify the Constitution in 1790.',
    },
    {
      id: 'q_locate_2',
      type: 'map_locate',
      prompt: 'Locate New Hampshire — the northernmost New England colony.',
      selectable_entity_ids: ALL_COLONIES,
      correct_entity_id: 'NH',
      explanation:
        "New Hampshire was established in 1623 as one of the earliest English settlements in North America. It separated from Massachusetts Bay Colony in 1679 as a royal colony. Its economy centered on timber, fishing, and trade from Portsmouth, its only port.",
    },
    {
      id: 'q_locate_3',
      type: 'map_locate',
      prompt: "Click on Delaware — the 'First State,' which ratified the Constitution before any other.",
      selectable_entity_ids: ALL_COLONIES,
      correct_entity_id: 'DE',
      explanation:
        'Delaware was the first of the original thirteen states to ratify the United States Constitution on December 7, 1787 — earning it the nickname "The First State." Originally settled by the Dutch and Swedes, it came under English control in 1664 and was linked to Pennsylvania under William Penn before gaining its own colonial assembly in 1704.',
    },
    {
      id: 'q8',
      type: 'map_short_answer',
      prompt:
        'Briefly explain why the highlighted colony attracted such a religiously diverse population.',
      highlighted_entity_ids: ['PA'],
      model_answer:
        "William Penn's 'Holy Experiment' guaranteed religious freedom for anyone who acknowledged one God, and the colony required no religious test for civil office. Combined with fertile land, generous land grants, and active recruitment in continental Europe, this drew Quakers, German Lutherans, Mennonites, Moravians, Scots-Irish Presbyterians, and others. The result was the most religiously and ethnically diverse of the thirteen colonies.",
    },
  ],
};
